import styled from '@emotion/styled';
import { useRef } from 'react';
import { mergeProps, useButton, useFocusRing, useOverlayTrigger } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';

import { su, suPx } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { AnimationRemain } from '../../../components/animation/AnimationRemain';
import { Glyph } from '../../../components/common/Glyph';
import { Overlay } from '../../../components/overlay/Overlay';

import { TokenListBox } from './TokenListBox';

export function TokenPicker(props) {
  const triggerRef = useRef();
  const overlayTriggerState = useOverlayTriggerState({
    defaultOpen: props.defaultOpen,
    onOpenChange(isOpen) {
      props.onOpenChange?.(isOpen);
    },
  });
  const overlayTrigger = useOverlayTrigger({ type: 'listbox' }, overlayTriggerState, triggerRef);

  const focusRing = useFocusRing();
  const button = useButton(
    {
      ...overlayTrigger.triggerProps,
      elementType: props.triggerElementType,
      isDisabled: props.isTriggerDisabled,
      onPress: () => {
        overlayTriggerState.toggle();
      },
      onKeyDown(event) {
        if (event.key === 'Delete') {
          event.preventDefault();
          props.onClearRequest?.();
        } else {
          event.continuePropagation();
        }
      },
    },
    triggerRef,
  );

  return (
    <>
      {props.renderTrigger ? (
        props.renderTrigger(triggerRef, {
          ...mergeProps(button.buttonProps, focusRing.focusProps),
          'data-is-disabled': props.isTriggerDisabled,
          'data-is-disabled-style': props.isTriggerDisabledStyle,
          // 'data-is-focus-visible': focusRing.isFocusVisible,
        })
      ) : (
        <TokenPicker.Root
          {...button.buttonProps}
          // Not implemented for default button yet
          // data-is-disabled: props.isTriggerDisabled
          // data-is-disabled-style: props.isTriggerDisabledStyle
          data-is-open={overlayTriggerState.isOpen}
          ref={triggerRef}
        >
          <TokenPicker.Glyph
            source={glyph.core.tagFill}
            size={props.triggerIconSize}
            color={'var(--module-glyph-color)'}
          />
        </TokenPicker.Root>
      )}

      <AnimationRemain condition={overlayTriggerState.isOpen} delay={200}>
        {(animationRemainProps) => {
          return (
            <Overlay
              targetRef={triggerRef}
              overlayProps={overlayTrigger.overlayProps}
              overlayTriggerState={overlayTriggerState}
              offset={suPx(1)}
              animationRemainProps={animationRemainProps}
              autoFocus={props.autoFocus ?? true}
              restoreFocus={props.restoreFocus ?? true}
              containFocus={props.containFocus}
            >
              <TokenListBox
                types={props.types}
                onSelect={(...args) => {
                  overlayTriggerState.close();
                  props.onSelect(...args);
                }}
              />
            </Overlay>
          );
        }}
      </AnimationRemain>
    </>
  );
}

TokenPicker.Root = styled.button`
  --module-glyph-color: ${theme.color.icon_light};

  position: relative;
  align-items: center;
  flex: 0 0 auto;
  padding: ${su(1)};

  &:hover {
    --module-glyph-color: ${theme.color.icon_light_hover};
  }

  &[data-is-open='true'] {
    --module-glyph-color: ${theme.color.highlight};
  }
`;

TokenPicker.Glyph = styled(Glyph)`
  transition: background-color ${motion.transition.micro_partial};
`;
